import { useMemo, useState } from "react";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react";
import { Link } from "react-router";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CapacityBulletBar } from "@/components/capacity/CapacityBulletBar";
import { CapacityViewSwitch } from "@/components/capacity/CapacityViewSwitch";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { useCapacityViewPreference } from "@/hooks/useCapacityViewPreference";
import { useCurrentCapacityThresholds } from "@/hooks/useCapacitySettings";
import { getCapacityColor } from "@/lib/capacityColor";
import { formatDecimal, formatNumber, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  occupancyAccent,
  occupancySections,
  toOccupancyGroup,
  type OccupancyAccent,
  type OccupancyGroup,
} from "@/lib/analytics/occupancy";
import { FormulaTip } from "@/components/ui/formula-tip";
import { FORMULA } from "@/lib/analytics/formulas";

const GROUPS_COLLAPSED_COUNT = 12;

const ACCENT_TONE: Record<OccupancyAccent, StatusTone> = {
  good: "success",
  warn: "warning",
  bad: "danger",
  neutral: "neutral",
};

interface ServiceSlice {
  service_code?: string;
  service_name?: string;
  time_range?: string;
  headcount?: number;
  max_headcount?: number;
  weighted_headcount?: number;
  max_weighted_headcount?: number;
  weighted_utilization_percent?: number;
  headcount_utilization_percent?: number;
  utilization_percent?: number;
  status?: string;
}

export interface GroupOverviewGroup {
  group_id?: string;
  group_name?: string;
  /** The group's location; filled from the group list when the overview omits it. */
  childcare_id?: string;
  headcount?: number;
  max_headcount?: number;
  weighted_headcount?: number;
  max_weighted_headcount?: number;
  weighted_utilization_percent?: number;
  headcount_utilization_percent?: number;
  present_count?: number;
  expected_count?: number;
  status?: string;
  peak_service?: string;
  services?: ServiceSlice[];
}

export interface GroupOverviewProps {
  groups: GroupOverviewGroup[];
  /** The organisation's locations, for the section names and order. */
  childcares?: readonly { id: string; name: string }[];
  /** The selected day's planned coverage per group, for the "Staff short" badge. */
  coverageGroups?: readonly { group_id?: string; status?: string }[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onNavigate: (groupId?: string) => void;
  resolveServiceName: (code: string, fallback?: string) => string;
  className?: string;
}

/** The fields `occupancySections` reads, keeping the card's own group. */
function toSectionGroup(g: GroupOverviewGroup): OccupancyGroup & { source: GroupOverviewGroup } {
  return { ...toOccupancyGroup(g), source: g };
}

/**
 * The Dashboard's group cards (#2175 C3, the phone's GroupOverviewList): with
 * more than one location the groups sit under their location, each location
 * with its summed weighted occupancy (`occupancy.ts`) and a fold. A group whose
 * planned coverage for the day is tight or critical carries "Staff short". The
 * per-service breakdown (hover) and the PEAK / service switch stay.
 */
export function GroupOverview({
  groups,
  childcares,
  coverageGroups,
  isLoading,
  isError,
  error,
  onRetry,
  onNavigate,
  resolveServiceName,
  className,
}: GroupOverviewProps) {
  const [expanded, setExpanded] = useState(false);
  const [folded, setFolded] = useState<ReadonlySet<string>>(new Set());
  const { i18n } = useLingui();
  const locale = i18n.locale;
  const { warningThreshold, criticalThreshold } = useCurrentCapacityThresholds();

  // Flatten group.services so the switch can rank them once across all groups.
  const flatServices = useMemo(() => groups.flatMap((g) => g.services ?? []), [groups]);

  // Codes the switch will offer (excludes FULL_DAY) — used to validate persisted view.
  const availableCodes = useMemo(() => {
    const codes = new Set<string>();
    for (const s of flatServices) {
      if (s.service_code && s.service_code.toUpperCase() !== "FULL_DAY") {
        codes.add(s.service_code);
      }
    }
    return codes;
  }, [flatServices]);

  const [view, setView] = useCapacityViewPreference();

  // If persisted view no longer maps to an available service, fall back to PEAK.
  const effectiveView = view === "PEAK" || availableCodes.has(view) ? view : "PEAK";

  const coverageById = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const c of coverageGroups ?? []) if (c.group_id) map.set(c.group_id, c.status);
    return map;
  }, [coverageGroups]);

  const sections = useMemo(
    () => occupancySections(groups.map(toSectionGroup), childcares),
    [groups, childcares],
  );
  const byLocation = sections.length > 1;

  // Collapsed, the first twelve groups in section order show; the rest wait for "Show all".
  const visibleIds = useMemo(() => {
    const ordered = sections.flatMap((s) => s.groups.map((g) => g.source));
    return new Set((expanded ? ordered : ordered.slice(0, GROUPS_COLLAPSED_COUNT)).map((g) => g.group_id));
  }, [sections, expanded]);
  const hasMore = groups.length > GROUPS_COLLAPSED_COUNT;
  const ratio = (value: number, max: number | undefined, digits = 0) =>
    `${formatDecimal(value, locale, digits)}${typeof max === "number" ? `/${formatDecimal(max, locale, digits)}` : ""}`;
  const toggleFold = (key: string) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const renderGroup = (group: GroupOverviewGroup) => {
    // Pick the slice driving the displayed numbers
    let slice: ServiceSlice | null = null;
    let sliceLabel: string | null = null;
    if (effectiveView !== "PEAK") {
      const found = (group.services ?? []).find((s) => s.service_code === effectiveView);
      if (found) {
        slice = found;
        sliceLabel = found.service_code ? resolveServiceName(found.service_code, found.service_name) : null;
      }
    }

    const utilization = slice
      ? (slice.weighted_utilization_percent ?? slice.headcount_utilization_percent ?? slice.utilization_percent ?? 0)
      : (group.weighted_utilization_percent ?? group.headcount_utilization_percent ?? 0);
    const weightedHc = slice ? (slice.weighted_headcount ?? slice.headcount ?? 0) : (group.weighted_headcount ?? group.headcount ?? 0);
    const maxWeightedHc = slice
      ? slice.max_weighted_headcount ?? slice.max_headcount
      : group.max_weighted_headcount ?? group.max_headcount;
    const headcount = slice ? (slice.headcount ?? 0) : (group.headcount ?? 0);
    const maxHeadcount = slice ? slice.max_headcount : group.max_headcount;
    const isEmpty = weightedHc === 0;
    // Colour comes from the configured warning/critical thresholds
    // (green/yellow/red), not the backend status enum — so the card
    // matches the Settings "Capacity Badge Preview" in every theme.
    const colors = getCapacityColor(utilization, { warningThreshold, criticalThreshold });
    const borderColor = isEmpty ? "border-border" : colors.border;
    // Set by the overview for today only; the whole-day roster, not one slot.
    const hasArrivals = typeof group.present_count === "number" && typeof group.expected_count === "number";
    const coverageStatus = group.group_id ? coverageById.get(group.group_id) : undefined;
    const staffShort = coverageStatus === "WARNING" || coverageStatus === "CRITICAL";

    const card = (
      <button
        type="button"
        className={cn(
          "w-full rounded-lg border p-3 text-left hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          borderColor,
        )}
        onClick={() => onNavigate(group.group_id)}
      >
        <div className="flex items-start justify-between mb-2 gap-1">
          <span className="text-sm font-medium truncate pr-1">{group.group_name}</span>
          <div className="text-xs text-muted-foreground flex-shrink-0 text-right leading-tight tabular-nums">
            <div>{ratio(headcount, maxHeadcount)}</div>
            <div>{ratio(weightedHc, maxWeightedHc, 1)}</div>
          </div>
        </div>
        {staffShort && (
          <StatusBadge
            className="mb-1"
            tone={coverageStatus === "CRITICAL" ? "danger" : "warning"}
            label={i18n._({ id: "dashboard.groups.staffShort", message: "Staff short" })}
          />
        )}
        {hasArrivals && (
          <div className="text-xs text-muted-foreground mb-1 tabular-nums">
            {i18n._({
              id: "dashboard.groups.arrived",
              message: "{present} of {expected} arrived",
              values: {
                present: formatNumber(group.present_count, locale),
                expected: formatNumber(group.expected_count, locale),
              },
            })}
          </div>
        )}
        {sliceLabel && (
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 truncate">{sliceLabel}</div>
        )}
        {effectiveView === "PEAK" && group.peak_service && (
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 truncate">
            ★ {resolveServiceName(group.peak_service)}
          </div>
        )}
        {/* Bullet bar (CAP-3/C5, D7) — the same component the capacity grid uses,
            so the Dashboard group cards read identically. */}
        <CapacityBulletBar value={utilization} thresholds={{ warningThreshold, criticalThreshold }} treatZeroAsEmpty={isEmpty} />
      </button>
    );

    const services = group.services ?? [];

    if (services.length === 0) {
      return <div key={group.group_id}>{card}</div>;
    }

    return (
      <HoverCard key={group.group_id} openDelay={150} closeDelay={100}>
        <HoverCardTrigger asChild>
          <div>{card}</div>
        </HoverCardTrigger>
        <HoverCardContent className="w-96 p-0" align="start">
          <div className="px-3 py-2 border-b">
            <div className="text-sm font-medium">{group.group_name}</div>
            <div className="text-xs text-muted-foreground">
              <Trans id="dashboard.groups.popover.subtitle">Service breakdown</Trans>
            </div>
          </div>
          <div className="grid grid-cols-[1fr_4rem_5rem] gap-x-6 px-3 py-1.5 text-xs uppercase tracking-wide text-muted-foreground border-b">
            <span>
              <Trans id="dashboard.groups.popover.service">Service</Trans>
            </span>
            <span className="text-right">
              <Trans id="dashboard.groups.popover.headcount">Headcount</Trans>
            </span>
            <span className="text-right">
              <Trans id="dashboard.groups.popover.weighted">Weighted</Trans>
            </span>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {services.map((s) => {
              const isPeak = group.peak_service === s.service_code;
              return (
                <div
                  key={s.service_code}
                  className={cn(
                    "grid grid-cols-[1fr_4rem_5rem] gap-x-6 items-center px-3 py-1.5 text-xs tabular-nums",
                    isPeak && "bg-accent/60",
                  )}
                >
                  <div className="flex flex-col min-w-0 pr-2">
                    <span className="font-medium truncate">
                      {isPeak ? "★ " : ""}
                      {s.service_code ? resolveServiceName(s.service_code, s.service_name) : s.service_name || ""}
                    </span>
                    {s.time_range && <span className="text-muted-foreground">{s.time_range}</span>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-medium">
                      {formatPercent((s.headcount_utilization_percent ?? 0) / 100, locale, 0)}
                    </div>
                    <div className="text-muted-foreground">{ratio(s.headcount ?? 0, s.max_headcount)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-medium">
                      {formatPercent((s.weighted_utilization_percent ?? 0) / 100, locale, 0)}
                    </div>
                    <div className="text-muted-foreground">{ratio(s.weighted_headcount ?? 0, s.max_weighted_headcount, 1)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  };

  const grid = (list: GroupOverviewGroup[]) => (
    <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">{list.map(renderGroup)}</div>
  );

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-1 text-base">
          <Trans id="dashboard.groups.title">Group Overview</Trans>
          <FormulaTip content={i18n._(FORMULA.dashboardGroupOverview)} />
        </CardTitle>
        <CapacityViewSwitch
          services={flatServices}
          value={effectiveView}
          onChange={setView}
          resolveServiceName={resolveServiceName}
        />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="h-20 rounded-lg" />
            ))}
          </div>
        ) : isError ? (
          <QueryErrorState error={error} onRetry={onRetry} />
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-4 text-center">
            <p className="text-sm text-muted-foreground">
              <Trans id="dashboard.groups.empty">No groups configured</Trans>
            </p>
            {/* The empty state says what to do next (#2175 UX pass). */}
            <Link
              to="/settings/groups"
              className="text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {i18n._({ id: "dashboard.setup.item.group", message: "Create a group" })}
            </Link>
          </div>
        ) : (
          <>
            {byLocation ? (
              <div className="space-y-4">
                {sections.map((section) => {
                  const shown = section.groups.map((g) => g.source).filter((g) => visibleIds.has(g.group_id));
                  if (shown.length === 0) return null;
                  const isFolded = folded.has(section.key);
                  const pct = section.figures.percent;
                  const name =
                    section.name ??
                    i18n._({ id: "dashboard.groups.otherGroups", message: "Other groups" });
                  const panelId = `dashboard-location-${section.key}`;
                  return (
                    <section key={section.key} data-testid={`dashboard-location-${section.key}`} className="space-y-2">
                      <button
                        type="button"
                        aria-expanded={!isFolded}
                        aria-controls={panelId}
                        onClick={() => toggleFold(section.key)}
                        className="flex w-full items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {isFolded ? (
                          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        ) : (
                          <ChevronUpIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold">{name}</span>
                          <span className="block text-xs text-muted-foreground tabular-nums">
                            {i18n._({
                              id: "dashboard.groups.locationFigures",
                              message: "{children, plural, one {# child} other {# children}} · {places, plural, one {# place} other {# places}}",
                              values: { children: section.figures.children, places: section.figures.places },
                            })}
                          </span>
                        </span>
                        {pct !== null && (
                          <StatusBadge
                            tone={ACCENT_TONE[occupancyAccent(pct)]}
                            label={formatPercent(pct / 100, locale, 0)}
                          />
                        )}
                      </button>
                      {!isFolded && <div id={panelId}>{grid(shown)}</div>}
                    </section>
                  );
                })}
              </div>
            ) : (
              grid(groups.filter((g) => visibleIds.has(g.group_id)))
            )}
            {hasMore && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-3 text-muted-foreground"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? (
                  <>
                    <ChevronUpIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                    {i18n._({ id: "dashboard.groups.showLess", message: "Show less" })}
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="h-4 w-4 mr-1" aria-hidden="true" />
                    {i18n._({ id: "dashboard.groups.showAll", message: "Show all {count} groups" }, { count: groups.length })}
                  </>
                )}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
